import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import complianceReportsCustomizationService from '../../../services/complianceReportsCustomizationService';

const ReportCustomizationPanel = ({ onReportGenerated }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customization, setCustomization] = useState({
    includeViolationTrends: true,
    includeDepartmentMetrics: true,
    includeCostAnalysis: true,
    includeDlirReadiness: true,
    periodStart: '',
    periodEnd: '',
    departments: [],
    reportFormat: 'pdf'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const departmentOptions = ['HR', 'Finance', 'Operations', 'IT', 'Sales', 'Marketing', 'Legal'];
  const formatOptions = [
    { value: 'pdf', label: 'PDF' },
    { value: 'excel', label: 'Excel' },
    { value: 'csv', label: 'CSV' }
  ];

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await complianceReportsCustomizationService?.getAllTemplates();
      setTemplates(data);
      
      const defaultTemplate = data?.find(t => t?.isDefault);
      if (defaultTemplate) {
        setSelectedTemplate(defaultTemplate?.id);
        applyTemplate(defaultTemplate);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load templates');
    }
  };

  const applyTemplate = (template) => {
    setCustomization(prev => ({
      ...prev,
      includeViolationTrends: template?.includeViolationTrends,
      includeDepartmentMetrics: template?.includeDepartmentMetrics,
      includeCostAnalysis: template?.includeCostAnalysis,
      includeDlirReadiness: template?.includeDlirReadiness
    }));
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates?.find(t => t?.id === templateId);
    if (template) {
      applyTemplate(template);
    }
  };

  const handleDepartmentToggle = (dept) => {
    setCustomization(prev => ({
      ...prev,
      departments: prev?.departments?.includes(dept)
        ? prev?.departments?.filter(d => d !== dept)
        : [...prev?.departments, dept]
    }));
  };

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      setError('');

      if (!customization?.periodStart || !customization?.periodEnd) {
        setError('Please select report period');
        return;
      }

      // Generate violation trends if included
      if (customization?.includeViolationTrends) {
        await complianceReportsCustomizationService?.generateViolationTrends(
          customization?.periodStart,
          customization?.periodEnd
        );
      }

      onReportGenerated?.(customization);
    } catch (err) {
      setError(err?.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-md p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Settings" size={20} />
          Customize Compliance Report
        </h3>
      </div>

      {error && (
        <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md flex items-start gap-2">
          <Icon name="AlertCircle" size={18} className="mt-0.5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Report Template
          </label>
          <Select
            value={selectedTemplate}
            onChange={(e) => handleTemplateChange(e?.target?.value)}
            options={templates?.map(t => ({
              value: t?.id,
              label: t?.templateName
            }))}
            placeholder="Select template"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Period Start
            </label>
            <Input
              type="date"
              value={customization?.periodStart}
              onChange={(e) => setCustomization({ ...customization, periodStart: e?.target?.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Period End
            </label>
            <Input
              type="date"
              value={customization?.periodEnd}
              onChange={(e) => setCustomization({ ...customization, periodEnd: e?.target?.value })}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Report Sections
          </label>
          <div className="space-y-2">
            <Checkbox
              checked={customization?.includeViolationTrends}
              onChange={(e) => setCustomization({ ...customization, includeViolationTrends: e?.target?.checked })}
              label="Violation Trends Analysis"
              description="Historical violation patterns and trends"
            />
            <Checkbox
              checked={customization?.includeDepartmentMetrics}
              onChange={(e) => setCustomization({ ...customization, includeDepartmentMetrics: e?.target?.checked })}
              label="Department Performance Metrics"
              description="Compliance scores by department"
            />
            <Checkbox
              checked={customization?.includeCostAnalysis}
              onChange={(e) => setCustomization({ ...customization, includeCostAnalysis: e?.target?.checked })}
              label="Cost Impact Analysis (PNG Kina)"
              description="Financial impact of violations and remediation"
            />
            <Checkbox
              checked={customization?.includeDlirReadiness}
              onChange={(e) => setCustomization({ ...customization, includeDlirReadiness: e?.target?.checked })}
              label="DLIR Submission Readiness"
              description="Checklist for government submission"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Filter by Departments
          </label>
          <div className="grid grid-cols-2 gap-3">
            {departmentOptions?.map((dept) => (
              <div key={dept} className="flex items-center gap-2">
                <Checkbox
                  checked={customization?.departments?.includes(dept)}
                  onChange={() => handleDepartmentToggle(dept)}
                />
                <span className="text-sm text-foreground">{dept}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Report Format
          </label>
          <Select
            value={customization?.reportFormat}
            onChange={(e) => setCustomization({ ...customization, reportFormat: e?.target?.value })}
            options={formatOptions}
          />
        </div>

        <div className="flex items-center gap-3 pt-4 border-t border-border">
          <Button
            variant="primary"
            iconName="FileText"
            onClick={handleGenerateReport}
            loading={loading}
            disabled={!customization?.periodStart || !customization?.periodEnd}
          >
            Generate Report
          </Button>
          <Button
            variant="outline"
            iconName="Download"
            disabled={loading}
          >
            Export Configuration
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportCustomizationPanel;